/**
 * Vincent B677 (Eric 2026-02-XX) — Modale de détection et fusion des doublons
 * dans le dictionnaire.
 *
 * Affiche chaque groupe de doublons détecté par POST /api/jargon/detect-duplicates.
 * Pour chaque groupe :
 *   - Choix radio du terme principal (celui qui recevra la fusion)
 *   - Aperçu des termes doublons (définition, alias, sources)
 *   - Bouton "Fusionner ce groupe" → POST /api/jargon/merge
 *
 * La fusion préserve : définition consolidée, union des alias, union des
 * source_video_ids ET source_text_doc_ids (forward-compatible).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Warning, Sparkle, SpinnerGap } from "@phosphor-icons/react";
import { useLang } from "../../contexts/LanguageContext";
import { API_BASE as API } from "../../lib/apiBase";
import { confirmDialog } from "../../lib/uiDialogs";

const REASON_LABELS = {
  exact_case_insensitive_match: {
    fr: "Terme identique (casse ignorée)",
    en: "Exact term match (case-insensitive)",
  },
  alias_cross_reference: {
    fr: "Alias croisé",
    en: "Cross-referenced alias",
  },
  transitive_group: {
    fr: "Groupe transitif",
    en: "Transitive group",
  },
};

function reasonLabel(reason, lang) {
  if (reason?.startsWith("token_similarity_")) {
    return lang === "fr"
      ? `Similarité de mots (${reason.replace("token_similarity_", "")})`
      : `Word similarity (${reason.replace("token_similarity_", "")})`;
  }
  const entry = REASON_LABELS[reason];
  return entry ? entry[lang] || entry.fr : reason;
}

// Bogue 1198 (Éric 2026-06-25) — `focusEntry` : ouvert depuis le badge
// « doublon ? » d'un terme, on ne montre QUE les groupes qui contiennent ce
// terme. Avant, le badge déversait les 62 groupes du dictionnaire et il
// fallait retrouver son mot à la main dans la liste.
export default function DuplicateReviewModal({ onClose, onMerged, manualGroup = null, focusEntry = null }) {
  const { lang } = useLang();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [selectedPrimary, setSelectedPrimary] = useState({}); // groupIndex -> entryId
  const [merging, setMerging] = useState(null); // groupIndex being merged
  // Vincent B960.b (Eric 2026-02-14 SPEC STRICK PROD) — Multi-select par
  // groupe. Permet de créer un sous-groupe à partir d'une sélection quand
  // le detector a regroupé à tort plusieurs concepts distincts (ex: « retour
  // d'impôts » + « taux marginal d'imposition »). splitSelection[groupIdx] =
  // Set<entryId> des membres cochés à séparer.
  const [splitSelection, setSplitSelection] = useState({});
  // Bogue 1191 — périmètre de la détection et nombre de voisins plus lointains
  // encore disponibles (proposés seulement après les fusions sûres).
  const [scope, setScope] = useState("identical");
  const [widerAvailable, setWiderAvailable] = useState(0);

  const runDetection = async (scope = "identical") => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/jargon/detect-duplicates?scope=${scope}`,
        {},
        { withCredentials: true, timeout: 60000 },
      );
      const all = data.duplicate_groups || [];
      // Bogue 1198 — sous-liste : uniquement les groupes où figure le terme cliqué.
      const focused = focusEntry?.id
        ? all.filter((g) => g.primary?.id === focusEntry.id
            || (g.duplicates || []).some((d) => d.id === focusEntry.id))
        : all;
      setGroups(focused);
      // Bogue 1191 (Éric 2026-06-25) — On ne propose d'abord que les titres
      // identiques ou emboîtés d'un mot. L'élargissement aux voisins plus
      // lointains est un second clic, explicite.
      setScope(data.scope || scope);
      setWiderAvailable(data.wider_available || 0);
      // Init selected primary = celui déjà proposé par le backend
      const initSel = {};
      (data.duplicate_groups || []).forEach((g, idx) => {
        initSel[idx] = g.primary?.id;
      });
      setSelectedPrimary(initSel);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "fr" ? "Erreur détection" : "Detection error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Tâche 1172 (Eric 2026-06-17) — Fusion d'une sélection manuelle : on
    // réutilise cette modale (définitions côte à côte, synthèse IA, texte
    // final modifiable) sans relancer la détection, qui ne trouverait pas
    // forcément ces termes ensemble — c'est justement pourquoi Eric les
    // choisit à la main.
    if (manualGroup) {
      setGroups([manualGroup]);
      setSelectedPrimary({ 0: manualGroup.primary?.id });
      setLoading(false);
      return;
    }
    runDetection();
    loadRejections();   // Tâche 1212
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Tâche 1162 (Eric 2026-06-17) — Fusion guidée : les définitions côte à
  // côte, une proposition de synthèse écrite par l'IA (pas une
  // concaténation), et un texte final modifiable avant de fusionner.
  // Tâche 1212 (Eric 2026-06-26) — Rejet d'une proposition de doublon.
  // « Temporairement » ne vit que dans cette fenêtre : le couple revient au
  // prochain clic sur « Examiner », donc rien n'est enregistré. « Définitivement »
  // est une décision : elle est stockée, listée, et révocable.
  const [rejecting, setRejecting] = useState(null);
  const [rejections, setRejections] = useState([]);
  const [showRejections, setShowRejections] = useState(false);

  const rejectTemporarily = (gIdx) => {
    setGroups((prev) => prev.filter((_, i) => i !== gIdx));
    toast.info(lang === "fr"
      ? "Proposition écartée pour cette session — elle reviendra au prochain examen."
      : "Dismissed for this session — it will come back next time.");
  };

  const rejectPermanently = async (gIdx) => {
    const group = groups[gIdx];
    if (!group) return;
    setRejecting(gIdx);
    try {
      await axios.post(`${API}/api/jargon/duplicates/reject`, {
        primary_id: group.primary.id,
        duplicate_ids: group.duplicates.map((d) => d.id),
        primary_term: group.primary.term,
        duplicate_terms: group.duplicates.map((d) => d.term),
      });
      setGroups((prev) => prev.filter((_, i) => i !== gIdx));
      toast.success(lang === "fr"
        ? "Rejeté définitivement — consultable via « Doublons rejetés »."
        : "Permanently rejected — see \"Rejected duplicates\".");
      loadRejections();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Rejet impossible" : "Reject failed"));
    } finally {
      setRejecting(null);
    }
  };

  const loadRejections = async () => {
    try {
      const { data } = await axios.get(`${API}/api/jargon/duplicates/rejections`);
      setRejections(data.rejections || []);
    } catch { /* liste non bloquante */ }
  };

  const undoRejection = async (pairKey) => {
    try {
      await axios.delete(`${API}/api/jargon/duplicates/rejections/${encodeURIComponent(pairKey)}`);
      toast.success(lang === "fr" ? "Rejet annulé — le couple sera reproposé." : "Rejection undone.");
      loadRejections();
      runDetection(scope);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const [guidedIdx, setGuidedIdx] = useState(null);
  const [guided, setGuided] = useState(null);
  const [guidedLoading, setGuidedLoading] = useState(false);
  const [finalText, setFinalText] = useState("");

  const openGuided = async (groupIdx) => {
    const group = groups[groupIdx];
    if (!group) return;
    if (guidedIdx === groupIdx) {
      setGuidedIdx(null);
      return;
    }
    setGuidedIdx(groupIdx);
    setGuided(null);
    setGuidedLoading(true);
    try {
      const ids = [group.primary, ...group.duplicates].map((e) => e.id);
      const { data } = await axios.post(
        `${API}/api/jargon/merge-preview`,
        { ids },
        { withCredentials: true, timeout: 60000 },
      );
      setGuided(data);
      setFinalText(data.suggestion || "");
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Aperçu impossible : " : "Preview failed: ") +
          (err.response?.data?.detail || err.message),
      );
      setGuidedIdx(null);
    } finally {
      setGuidedLoading(false);
    }
  };

  const handleMerge = async (groupIdx, finalDefinition = null) => {
    const group = groups[groupIdx];
    if (!group) return;
    const allInGroup = [group.primary, ...group.duplicates];
    const keepId = selectedPrimary[groupIdx] || group.primary.id;
    const mergeIds = allInGroup.filter((e) => e.id !== keepId).map((e) => e.id);
    const keepTerm = allInGroup.find((e) => e.id === keepId)?.term || "?";
    const mergedTerms = allInGroup.filter((e) => e.id !== keepId).map((e) => e.term).join(", ");
    const ok = await confirmDialog(
      lang === "fr"
        ? `Fusionner « ${mergedTerms} » DANS « ${keepTerm} » ?\n\n  • Les définitions seront consolidées\n  • Les alias, catégories et tags seront regroupés (union sans doublon)\n  • Les vidéos et fichiers texte sources seront préservés\n  • Les termes fusionnés seront ARCHIVÉS (audit Loi 25) puis supprimés\n\nCette action est IRRÉVERSIBLE côté UI (mais restaurable via l'historique).`
        : `Merge "${mergedTerms}" INTO "${keepTerm}"?\n\nDefinitions, aliases, categories, tags and video/text sources will be consolidated. Merged terms will be archived and deleted (Law 25 audit).`,
      { danger: true, confirmLabel: lang === "fr" ? "Fusionner" : "Merge" },
    );
    if (!ok) return;

    setMerging(groupIdx);
    const toastId = toast.loading(lang === "fr" ? "Fusion en cours…" : "Merging…", { duration: Infinity });
    try {
      const { data } = await axios.post(
        `${API}/api/jargon/merge`,
        finalDefinition
          ? { keep_id: keepId, merge_ids: mergeIds, definition: finalDefinition }
          : { keep_id: keepId, merge_ids: mergeIds },
        { withCredentials: true, timeout: 30000 },
      );
      toast.success(
        lang === "fr"
          ? `✅ ${data.merged_count} terme(s) fusionné(s) dans « ${keepTerm} ».`
          : `✅ ${data.merged_count} term(s) merged into "${keepTerm}".`,
        { id: toastId, duration: 6000 },
      );
      // Retire le groupe de la liste
      setGuidedIdx(null);
      setGuided(null);
      setGroups((prev) => prev.filter((_, i) => i !== groupIdx));
      onMerged?.(data);
    } catch (err) {
      toast.error(
        (lang === "fr" ? "❌ Fusion échouée : " : "❌ Merge failed: ") +
          (err.response?.data?.detail || err.message),
        { id: toastId, duration: 8000 },
      );
    } finally {
      setMerging(null);
    }
  };

  // Vincent B960.b (Eric 2026-02-14 SPEC STRICK PROD) — Sépare le groupe
  // en 2 : les membres cochés (splitSelection[groupIdx]) forment un nouveau
  // groupe indépendant, les autres restent dans le groupe original. La
  // fusion se pilote ensuite indépendamment sur chacun. Refuse si <2
  // membres sélectionnés ou si tous les membres sont sélectionnés (car
  // ça reviendrait à laisser le groupe intact).
  const toggleSplitMember = (groupIdx, entryId) => {
    setSplitSelection((prev) => {
      const cur = new Set(prev[groupIdx] || []);
      if (cur.has(entryId)) cur.delete(entryId);
      else cur.add(entryId);
      return { ...prev, [groupIdx]: cur };
    });
  };

  const splitGroup = (groupIdx) => {
    const group = groups[groupIdx];
    if (!group) return;
    const selected = splitSelection[groupIdx] || new Set();
    const allInGroup = [group.primary, ...group.duplicates];
    const selectedMembers = allInGroup.filter((e) => selected.has(e.id));
    const remainingMembers = allInGroup.filter((e) => !selected.has(e.id));
    if (selectedMembers.length < 2) {
      toast.error(
        lang === "fr"
          ? "Sélectionne au moins 2 termes pour créer un nouveau groupe."
          : "Select at least 2 terms to create a new group.",
      );
      return;
    }
    if (remainingMembers.length < 2) {
      // Le split laisserait moins de 2 membres → équivalent à retirer les
      // non-sélectionnés du dictionnaire des doublons. On l'accepte quand
      // même : le groupe résiduel devient un « singleton » qui disparaîtra.
      // Mais on prévient l'utilisateur.
      // eslint-disable-next-line no-alert
      if (!window.confirm(
        lang === "fr"
          ? `Le groupe original ne contiendra plus que ${remainingMembers.length} terme(s) après séparation — il sera retiré de la liste. Continuer ?`
          : `The original group will have only ${remainingMembers.length} term(s) left — it will be dropped. Continue?`,
      )) return;
    }
    // Construction des 2 nouveaux groupes. Le premier membre reste primary
    // (heuristique legacy — l'utilisateur peut ensuite basculer via radio).
    const buildGroup = (members) => ({
      primary: members[0],
      duplicates: members.slice(1),
      confidence: group.confidence,
      reason: `${group.reason}_split_manuel`,
    });
    const newGroups = [];
    if (remainingMembers.length >= 2) newGroups.push(buildGroup(remainingMembers));
    newGroups.push(buildGroup(selectedMembers));
    setGroups((prev) => {
      const next = [...prev];
      next.splice(groupIdx, 1, ...newGroups);
      return next;
    });
    setSelectedPrimary((prev) => {
      const next = { ...prev };
      // Le groupe original disparaît. Les nouveaux groupes prennent des
      // indices consécutifs — on ne les préconfigure pas (defaultent au
      // premier membre = primary via la logique de rendu).
      delete next[groupIdx];
      return next;
    });
    setSplitSelection((prev) => {
      const next = { ...prev };
      delete next[groupIdx];
      return next;
    });
    toast.success(
      lang === "fr"
        ? `✂️ Groupe séparé : ${remainingMembers.length} + ${selectedMembers.length} termes.`
        : `✂️ Group split: ${remainingMembers.length} + ${selectedMembers.length} terms.`,
      { duration: 4000 },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="duplicate-review-modal"
    >
      <div className="w-full max-w-3xl max-h-[85vh] overflow-hidden bg-white rounded-lg shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-amber-50">
          <div className="flex items-center gap-2">
            <Warning size={18} weight="bold" className="text-amber-700" />
            <h2 className="font-semibold text-sm">
              {focusEntry?.term
                ? (lang === "fr" ? `Doublons de « ${focusEntry.term} »` : `Duplicates of "${focusEntry.term}"`)
                : (lang === "fr" ? "Doublons détectés dans le dictionnaire" : "Duplicates detected in dictionary")}
            </h2>
            {/* Tâche 1212 — accès aux rejets définitifs, pour les annuler. */}
            {/* Toujours visible : Eric doit pouvoir VÉRIFIER qu'il n'a rien
                rejeté par erreur, y compris quand la liste est vide. */}
            {(
              <button
                onClick={() => setShowRejections((v) => !v)}
                className="text-[11px] font-semibold px-2 py-0.5 rounded border border-red-300 text-red-700 bg-white hover:bg-red-50"
                data-testid="show-rejections-btn"
              >
                {lang === "fr"
                  ? `Doublons rejetés (${rejections.length})`
                  : `Rejected duplicates (${rejections.length})`}
              </button>
            )}
            {!loading && (
              <span className="text-xs text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
                {groups.length} {lang === "fr" ? "groupe(s)" : "group(s)"}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-900"
            data-testid="close-duplicates-modal"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Tâche 1212 — Liste des rejets définitifs, avec annulation. */}
        {showRejections && (
          <div
            className="px-5 py-3 border-b border-red-200 bg-red-50/60"
            data-testid="rejections-panel"
          >
            <p className="text-xs text-red-900 font-semibold mb-2">
              {lang === "fr"
                ? "Rapprochements rejetés définitivement — annulez pour qu'ils soient reproposés"
                : "Permanently rejected pairs — undo to have them proposed again"}
            </p>
            {rejections.length === 0 && (
              <p className="text-xs text-zinc-600" data-testid="rejections-empty">
                {lang === "fr"
                  ? "Aucun rapprochement rejeté définitivement."
                  : "No permanently rejected pair."}
              </p>
            )}
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {rejections.map((r) => (
                <li
                  key={r.pair_key}
                  className="flex items-center justify-between gap-2 text-xs bg-white rounded border border-red-200 px-2 py-1"
                  data-testid={`rejection-row-${r.pair_key}`}
                >
                  <span className="truncate">
                    <strong>{r.primary_term || r.primary_id}</strong>
                    {" ≠ "}
                    <strong>{r.duplicate_term || r.duplicate_id}</strong>
                    <span className="text-zinc-500">
                      {r.rejected_by ? ` · ${r.rejected_by}` : ""}
                    </span>
                  </span>
                  <button
                    onClick={() => undoRejection(r.pair_key)}
                    className="shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50"
                    data-testid={`undo-rejection-${r.pair_key}`}
                  >
                    {lang === "fr" ? "Annuler le rejet" : "Undo"}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-zinc-500">
              <SpinnerGap size={18} className="animate-spin" />
              {lang === "fr" ? "Analyse du dictionnaire…" : "Analyzing dictionary…"}
            </div>
          ) : groups.length === 0 ? (
            <div className="py-16 text-center" data-testid="no-duplicates-empty">
              <Sparkle size={32} className="mx-auto text-emerald-500 mb-3" weight="bold" />
              <p className="text-sm font-medium text-zinc-700">
                {lang === "fr" ? "Aucun doublon détecté 🎉" : "No duplicates detected 🎉"}
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                {lang === "fr"
                  ? "Votre dictionnaire est propre — pas de termes redondants trouvés."
                  : "Your dictionary is clean — no redundant terms found."}
              </p>
              {scope === "identical" && widerAvailable > 0 && (
                <button
                  onClick={() => runDetection("extended")}
                  className="mt-4 text-xs font-semibold text-amber-800 hover:underline"
                  data-testid="widen-detection-empty-btn"
                >
                  {lang === "fr"
                    ? `Élargir : 2 mots d'écart et alias croisés (${widerAvailable} voisin(s))`
                    : `Widen: 2-word gap and cross aliases (${widerAvailable})`}
                </button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Vincent B678 (Eric 2026-02-XX) — Bouton bulk auto-merge :
                  fusionne en 1 clic tous les groupes à confidence=1.0 (matches
                  exacts et alias croisés). N'affiche que s'il y a au moins
                  1 groupe éligible. */}
              {(() => {
                const eligibleCount = groups.filter((g) => (g.confidence || 0) >= 1.0).length;
                if (eligibleCount === 0) return null;
                return (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-center justify-between" data-testid="bulk-auto-merge-banner">
                    <div className="text-xs text-emerald-900">
                      <span className="font-semibold">{eligibleCount}</span>{" "}
                      {lang === "fr"
                        ? `groupe${eligibleCount > 1 ? "s" : ""} détecté${eligibleCount > 1 ? "s" : ""} à 100 % de confiance — fusionnables automatiquement.`
                        : `group${eligibleCount > 1 ? "s" : ""} at 100% confidence — safe to auto-merge.`}
                    </div>
                    <button
                      onClick={async () => {
                        const ok = await confirmDialog(
                          lang === "fr"
                            ? `Fusionner automatiquement les ${eligibleCount} groupe(s) détecté(s) à 100 % de confiance ?\n\nCes doublons correspondent à des titres identiques (casse, accents, pluriel ignorés) — la fusion est très fiable. Les alias croisés et les titres emboîtés en sont exclus : ils attendent un clic. Chaque terme fusionné est archivé dans jargon_history (audit Loi 25).\n\nContinuer ?`
                            : `Auto-merge the ${eligibleCount} group(s) at 100% confidence?`,
                          { danger: true, confirmLabel: lang === "fr" ? "Fusionner tout" : "Merge all" },
                        );
                        if (!ok) return;
                        setMerging(-1);
                        const toastId = toast.loading(lang === "fr" ? "Fusion automatique en cours…" : "Bulk merging…", { duration: Infinity });
                        try {
                          const { data } = await axios.post(
                            `${API}/api/jargon/bulk-merge-duplicates`,
                            { min_confidence: 1.0 },
                            { withCredentials: true, timeout: 120000 },
                          );
                          toast.success(
                            lang === "fr"
                              ? `✅ ${data.terms_merged} terme(s) fusionné(s) dans ${data.groups_processed} groupe(s).`
                              : `✅ ${data.terms_merged} term(s) merged in ${data.groups_processed} group(s).`,
                            { id: toastId, duration: 8000 },
                          );
                          onMerged?.(data);
                          // Relance la détection pour retirer les groupes fusionnés
                          await runDetection(scope);
                        } catch (err) {
                          toast.error(
                            (lang === "fr" ? "❌ Échec : " : "❌ Failed: ") +
                              (err.response?.data?.detail || err.message),
                            { id: toastId, duration: 10000 },
                          );
                        } finally {
                          setMerging(null);
                        }
                      }}
                      disabled={merging !== null}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 shadow-sm"
                      data-testid="bulk-auto-merge-btn"
                    >
                      {merging === -1 ? <SpinnerGap size={12} className="animate-spin" /> : <Sparkle size={12} weight="bold" />}
                      {lang === "fr" ? "Fusionner tous les 100 %" : "Merge all 100%"}
                    </button>
                  </div>
                );
              })()}
              {groups.map((group, gIdx) => {
                const allInGroup = [group.primary, ...group.duplicates];
                const keepId = selectedPrimary[gIdx] || group.primary.id;
                const splitSet = splitSelection[gIdx] || new Set();
                const nbSelectedForSplit = splitSet.size;
                return (
                  <div
                    key={gIdx}
                    className="border border-zinc-200 rounded-lg overflow-hidden"
                    data-testid={`duplicate-group-${gIdx}`}
                  >
                    <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
                      <div className="text-xs text-zinc-700">
                        <span className="font-semibold">{allInGroup.length} {lang === "fr" ? "termes" : "terms"}</span>
                        {" · "}
                        {/* Tâche 1129 — Deux niveaux : quasi identique
                            (fusionnable sans risque) ou emboîté (à confirmer,
                            jamais fusionné d'office). */}
                        {group.needs_review ? (
                          <span
                            className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 font-semibold"
                            data-testid={`duplicate-group-review-${gIdx}`}
                          >
                            {lang === "fr" ? "à confirmer" : "needs review"}
                          </span>
                        ) : (
                          <span
                            className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-900 font-semibold"
                            data-testid={`duplicate-group-identical-${gIdx}`}
                          >
                            {lang === "fr" ? "quasi identique" : "near identical"}
                          </span>
                        )}
                        {" · "}
                        <span className="text-zinc-500">{reasonLabel(group.reason, lang)}</span>
                        {" · "}
                        <span className="text-zinc-500">
                          {lang === "fr" ? "confiance" : "confidence"}: {(group.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Vincent B960.b — Bouton « Séparer » actif dès que
                            2+ membres cochés. */}
                        {nbSelectedForSplit >= 2 && (
                          <button
                            onClick={() => splitGroup(gIdx)}
                            disabled={merging !== null}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded bg-amber-100 text-amber-900 hover:bg-amber-200 border border-amber-300 disabled:opacity-50"
                            data-testid={`split-group-btn-${gIdx}`}
                            title={lang === "fr" ? "Créer un nouveau groupe à partir des termes cochés" : "Create a new group from checked terms"}
                          >
                            {lang === "fr"
                              ? `✂️ Séparer (${nbSelectedForSplit})`
                              : `✂️ Split (${nbSelectedForSplit})`}
                          </button>
                        )}
                        <button
                          onClick={() => openGuided(gIdx)}
                          disabled={merging !== null}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded bg-white text-[#002FA7] border border-[#002FA7] hover:bg-blue-50 disabled:opacity-50"
                          data-testid={`guided-merge-btn-${gIdx}`}
                          title={lang === "fr" ? "Voir les définitions côte à côte avant de fusionner" : "Compare definitions before merging"}
                        >
                          {lang === "fr" ? "Fusion guidée" : "Guided merge"}
                        </button>
                        <button
                          onClick={() => handleMerge(gIdx)}
                          disabled={merging !== null}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded bg-[#002FA7] text-white hover:bg-[#001f70] disabled:opacity-50"
                          data-testid={`merge-group-btn-${gIdx}`}
                        >
                          {merging === gIdx ? <SpinnerGap size={12} className="animate-spin" /> : null}
                          {lang === "fr" ? "Fusionner ce groupe" : "Merge group"}
                        </button>
                        {/* Tâche 1212 — Refuser une proposition sans fusionner. */}
                        <button
                          onClick={() => rejectTemporarily(gIdx)}
                          disabled={merging !== null || rejecting !== null}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
                          data-testid={`reject-temp-btn-${gIdx}`}
                          title={lang === "fr"
                            ? "Écarter pour cette session : la proposition reviendra au prochain examen"
                            : "Dismiss for this session"}
                        >
                          {lang === "fr" ? "Rejeter temporairement" : "Reject for now"}
                        </button>
                        <button
                          onClick={() => rejectPermanently(gIdx)}
                          disabled={merging !== null || rejecting !== null}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded bg-white text-red-700 border border-red-300 hover:bg-red-50 disabled:opacity-50"
                          data-testid={`reject-perm-btn-${gIdx}`}
                          title={lang === "fr"
                            ? "Ne plus jamais proposer ce rapprochement (révocable via « Doublons rejetés »)"
                            : "Never propose again (revocable)"}
                        >
                          {rejecting === gIdx ? <SpinnerGap size={12} className="animate-spin" /> : null}
                          {lang === "fr" ? "Rejeter définitivement" : "Reject permanently"}
                        </button>
                      </div>
                    </div>
                    {guidedIdx === gIdx && (
                      <div
                        className="px-3 py-3 bg-blue-50/40 border-b border-blue-200"
                        data-testid={`guided-merge-panel-${gIdx}`}
                      >
                        {guidedLoading && (
                          <p className="text-xs text-zinc-600" data-testid="guided-merge-loading">
                            {lang === "fr" ? "Lecture des définitions…" : "Loading definitions…"}
                          </p>
                        )}
                        {guided && (
                          <>
                            <div className="grid gap-2 md:grid-cols-2">
                              {(guided.entries || []).map((en) => (
                                <div
                                  key={en.id}
                                  className="rounded-lg border border-zinc-200 bg-white p-2"
                                  data-testid={`guided-merge-entry-${en.id}`}
                                >
                                  <p className="text-xs font-semibold text-zinc-900 break-words">
                                    {en.term}
                                  </p>
                                  <p className="mt-1 text-xs text-zinc-600 whitespace-pre-wrap max-h-32 overflow-y-auto">
                                    {en.definition || (lang === "fr" ? "(aucune définition)" : "(no definition)")}
                                  </p>
                                  <button
                                    onClick={() => setFinalText(en.definition || "")}
                                    className="mt-1.5 text-[11px] px-2 py-0.5 rounded border border-zinc-300 hover:bg-zinc-50"
                                    data-testid={`guided-merge-pick-${en.id}`}
                                  >
                                    {lang === "fr" ? "Garder ce texte" : "Keep this text"}
                                  </button>
                                </div>
                              ))}
                            </div>
                            <div className="mt-2">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">
                                  {lang === "fr" ? "Texte final" : "Final text"}
                                  <span className="ml-2 normal-case text-zinc-400">
                                    {guided.suggestion_source === "ia"
                                      ? (lang === "fr" ? "proposition de l'IA, modifiable" : "AI suggestion, editable")
                                      : (lang === "fr" ? "définition la plus complète, modifiable" : "longest definition, editable")}
                                  </span>
                                </p>
                                <button
                                  onClick={() => setFinalText(guided.suggestion || "")}
                                  className="text-[11px] px-2 py-0.5 rounded border border-zinc-300 hover:bg-zinc-50"
                                  data-testid={`guided-merge-use-suggestion-${gIdx}`}
                                >
                                  {lang === "fr" ? "Reprendre la proposition" : "Use suggestion"}
                                </button>
                              </div>
                              <textarea
                                value={finalText}
                                onChange={(ev) => setFinalText(ev.target.value)}
                                rows={5}
                                className="mt-1 w-full rounded-lg border border-zinc-300 px-2 py-1.5 text-xs"
                                data-testid={`guided-merge-textarea-${gIdx}`}
                              />
                              <button
                                onClick={() => handleMerge(gIdx, finalText)}
                                disabled={merging !== null || !finalText.trim()}
                                className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded bg-[#002FA7] text-white hover:bg-[#001f70] disabled:opacity-40"
                                data-testid={`guided-merge-confirm-${gIdx}`}
                              >
                                {lang === "fr" ? "Fusionner avec ce texte" : "Merge with this text"}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    <div className="divide-y divide-zinc-100">
                      {allInGroup.map((e) => {
                        const isKeep = e.id === keepId;
                        const isSplitSelected = splitSet.has(e.id);
                        const nbSources =
                          (e.source_video_ids?.length || 0) + (e.source_text_doc_ids?.length || 0);
                        return (
                          <label
                            key={e.id}
                            className={`flex items-start gap-3 p-3 cursor-pointer transition-colors ${
                              isSplitSelected
                                ? "bg-amber-50"
                                : isKeep
                                  ? "bg-emerald-50"
                                  : "hover:bg-zinc-50"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`keep-${gIdx}`}
                              checked={isKeep}
                              onChange={() =>
                                setSelectedPrimary((prev) => ({ ...prev, [gIdx]: e.id }))
                              }
                              className="mt-1"
                              data-testid={`keep-radio-${gIdx}-${e.id}`}
                            />
                            {/* Vincent B960.b — Case à cocher pour split. */}
                            <input
                              type="checkbox"
                              checked={isSplitSelected}
                              onChange={(ev) => {
                                ev.stopPropagation();
                                toggleSplitMember(gIdx, e.id);
                              }}
                              onClick={(ev) => ev.stopPropagation()}
                              className="mt-1"
                              data-testid={`split-checkbox-${gIdx}-${e.id}`}
                              title={lang === "fr"
                                ? "Cocher pour extraire ce terme dans un nouveau groupe"
                                : "Check to extract this term into a new group"}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm">{e.term}</span>
                                {isKeep && (
                                  <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-emerald-600 text-white rounded">
                                    {lang === "fr" ? "À CONSERVER" : "TO KEEP"}
                                  </span>
                                )}
                                {nbSources > 0 && (
                                  <span className="px-1.5 py-0.5 text-[10px] bg-violet-50 text-violet-700 rounded border border-violet-100">
                                    {nbSources} {lang === "fr" ? "source(s)" : "source(s)"}
                                  </span>
                                )}
                              </div>
                              {/* Tâche 1464 (Eric 2026-06) — définition
                                  ENTIÈRE : c'est ICI qu'on décide d'une fusion,
                                  et trois lignes cachaient justement ce qui
                                  distingue deux fiches. */}
                              <p className="text-xs text-zinc-600 mt-1 whitespace-pre-line">
                                {e.definition || (lang === "fr" ? "(aucune définition)" : "(no definition)")}
                              </p>
                              {e.aliases && e.aliases.length > 0 && (
                                <div className="text-[10px] text-zinc-500 mt-1 truncate">
                                  {lang === "fr" ? "alias : " : "aliases: "}
                                  {e.aliases.join(", ")}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-between bg-zinc-50">
          <div className="flex items-center gap-4">
            <button
              onClick={() => runDetection(scope)}
              disabled={loading || merging !== null}
              className="text-xs text-[#002FA7] hover:underline disabled:opacity-50"
              data-testid="rerun-detection-btn"
            >
              {lang === "fr" ? "Relancer la détection" : "Rerun detection"}
            </button>
            {/* Bogue 1191 — L'élargissement au même champ lexical n'est
                proposé qu'APRÈS les titres identiques ou emboîtés d'un mot. */}
            {scope === "identical" && widerAvailable > 0 && (
              <button
                onClick={() => runDetection("extended")}
                disabled={loading || merging !== null}
                className="text-xs font-semibold text-amber-800 hover:underline disabled:opacity-50"
                data-testid="widen-detection-btn"
                title={lang === "fr"
                  ? "Ajoute les voisins plus lointains (deux mots d'écart, même champ lexical) — à trancher un par un"
                  : "Add looser neighbours (two extra words, same lexical field)"}
              >
                {lang === "fr"
                  ? `Élargir : 2 mots d'écart et alias croisés (${widerAvailable} voisin(s))`
                  : `Widen: 2-word gap and cross aliases (${widerAvailable})`}
              </button>
            )}
            {scope === "extended" && (
              <span className="text-xs text-amber-800" data-testid="widened-scope-note">
                {lang === "fr"
                  ? "Recherche élargie — chaque couple est à trancher à la main"
                  : "Widened search — each pair needs a human call"}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-xs font-semibold px-4 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50"
          >
            {lang === "fr" ? "Fermer" : "Close"}
          </button>
        </div>
      </div>
    </div>
  );
}
